import { useState } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Field } from "@/components/ui/Field";
import { Input, Textarea } from "@/components/ui/Input";
import { Table, Td, Th } from "@/components/ui/Table";
import { useReference } from "@/hooks/useReference";
import { useToast } from "@/hooks/useToast";
import { reference as referenceApi } from "@/lib/api";
import { strings } from "@/lib/strings";

type TableName = "organisms" | "antibiotics" | "wards" | "specimens";

/**
 * Reference data administration (docs/08 §8.8, A-04, A-05).
 *
 * Every change bumps the reference version and requires a note, so the reason
 * for a change is recoverable a year later.
 */
export function ReferenceAdmin() {
  const { organisms, antibiotics, wards, specimens, bundle, refresh } = useReference();
  const { notify } = useToast();

  const [addOpen, setAddOpen] = useState(false);
  const [table, setTable] = useState<TableName>("organisms");
  const [form, setForm] = useState({ code: "", name: "", note: "" });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      const result = await referenceApi.create(table, {
        code: form.code.trim(),
        name: form.name.trim(),
        note: form.note.trim(),
      });
      notify(
        `Added ${result.code}. Reference version is now ${result.reference_version}.`,
        "success",
      );
      setAddOpen(false);
      setForm({ code: "", name: "", note: "" });
      await refresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : strings.errors.generic, "error");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (tableName: TableName, code: string) => {
    try {
      await referenceApi.deactivate(tableName, code);
      notify(
        `${code} deactivated. Isolates already entered keep it.`,
        "success",
      );
      await refresh();
    } catch {
      notify(strings.errors.generic, "error");
    }
  };

  const openAdd = (tableName: TableName) => {
    setTable(tableName);
    setAddOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-900">
          {strings.admin.referenceData}
        </h1>
        <Badge tone="brand">Reference version {bundle?.version ?? "—"}</Badge>
      </div>

      <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
        Deactivating an entry never changes isolates that already use it: each
        isolate records the reference version in force when it was entered.
      </p>

      <Tabs.Root defaultValue="organisms">
        <Tabs.List className="flex gap-1 overflow-x-auto border-b border-slate-200">
          {(
            [
              ["organisms", `Organisms (${organisms.length})`],
              ["antibiotics", `Antibiotics (${antibiotics.length})`],
              ["wards", `Wards (${wards.length})`],
              ["specimens", `Specimens (${specimens.length})`],
            ] as const
          ).map(([value, label]) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className="whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-sm font-medium text-slate-600
                         data-[state=active]:border-brand-700 data-[state=active]:text-brand-900"
            >
              {label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="organisms" className="pt-4">
          <Card>
            <CardHeader
              title="Organisms"
              actions={<Button size="sm" onClick={() => openAdd("organisms")}>Add</Button>}
            />
            <Table caption="Organisms">
              <thead>
                <tr>
                  <Th>Code</Th>
                  <Th>Name</Th>
                  <Th>Gram</Th>
                  <Th><span className="sr-only">Actions</span></Th>
                </tr>
              </thead>
              <tbody>
                {organisms.map((organism) => (
                  <tr key={organism.code}>
                    <Td className="font-mono text-xs">{organism.code}</Td>
                    <Td className="italic">{organism.name}</Td>
                    <Td>{organism.gram}</Td>
                    <Td>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void deactivate("organisms", organism.code)}
                      >
                        Deactivate
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Tabs.Content>

        <Tabs.Content value="antibiotics" className="pt-4">
          <Card>
            <CardHeader
              title="Antibiotics"
              description={strings.admin.tbcNotice}
              actions={<Button size="sm" onClick={() => openAdd("antibiotics")}>Add</Button>}
            />
            <Table caption="Antibiotics">
              <thead>
                <tr>
                  <Th>WHONET code</Th>
                  <Th>Name</Th>
                  <Th>Class</Th>
                  <Th><span className="sr-only">Actions</span></Th>
                </tr>
              </thead>
              <tbody>
                {antibiotics.map((drug) => (
                  <tr key={drug.code}>
                    <Td className="font-mono text-xs">{drug.code}</Td>
                    <Td>
                      {drug.name}
                      {drug.is_tbc && (
                        <Badge tone="warning" className="ml-2">
                          to confirm
                        </Badge>
                      )}
                    </Td>
                    <Td className="text-slate-500">{drug.drug_class ?? "—"}</Td>
                    <Td>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void deactivate("antibiotics", drug.code)}
                      >
                        Deactivate
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Tabs.Content>

        <Tabs.Content value="wards" className="pt-4">
          <Card>
            <CardHeader
              title="Wards"
              actions={<Button size="sm" onClick={() => openAdd("wards")}>Add</Button>}
            />
            <Table caption="Wards">
              <thead>
                <tr>
                  <Th>Code</Th>
                  <Th>Name</Th>
                  <Th>Department</Th>
                  <Th>Type</Th>
                </tr>
              </thead>
              <tbody>
                {wards.map((ward) => (
                  <tr key={ward.code}>
                    <Td className="font-mono text-xs">{ward.code}</Td>
                    <Td>{ward.name}</Td>
                    <Td>{ward.department ?? "—"}</Td>
                    <Td>{ward.location_type}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Tabs.Content>

        <Tabs.Content value="specimens" className="pt-4">
          <Card>
            <CardHeader
              title="Specimen types"
              actions={<Button size="sm" onClick={() => openAdd("specimens")}>Add</Button>}
            />
            <Table caption="Specimen types">
              <thead>
                <tr>
                  <Th>Code</Th>
                  <Th>Name</Th>
                  <Th>WHONET</Th>
                  <Th>Flags</Th>
                </tr>
              </thead>
              <tbody>
                {specimens.map((specimen) => (
                  <tr key={specimen.code}>
                    <Td className="font-mono text-xs">{specimen.code}</Td>
                    <Td>{specimen.name}</Td>
                    <Td className="font-mono text-xs">{specimen.whonet_code ?? "—"}</Td>
                    <Td className="space-x-1">
                      {specimen.is_urine && <Badge tone="info">urine</Badge>}
                      {specimen.is_inpatient_typical && <Badge>inpatient</Badge>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Tabs.Content>
      </Tabs.Root>

      <Dialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title={`Add to ${table}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              {strings.common.cancel}
            </Button>
            <Button
              loading={busy}
              disabled={!form.code.trim() || !form.name.trim() || form.note.trim().length < 3}
              onClick={() => void add()}
            >
              Add
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field id="ref-code" label="Code" required>
            <Input
              id="ref-code"
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value })}
            />
          </Field>
          <Field id="ref-name" label="Name" required>
            <Input
              id="ref-name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </Field>
          <Field
            id="ref-note"
            label={strings.admin.changeNote}
            hint="Recorded in the audit log against your name."
            required
          >
            <Textarea
              id="ref-note"
              value={form.note}
              onChange={(event) => setForm({ ...form, note: event.target.value })}
              placeholder="Added Cefpodoxime after the microbiologist confirmed the Cefo mapping."
            />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
